import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Button,
  Select,
  FormError,
  RegistrationModal,
  BeadCrumb,
} from "components";
import { Formik, Form, ErrorMessage } from "formik";
import { FaRegUser } from "react-icons/fa";
import * as Yup from "yup";
import {
  capitalizeFirstLetter,
  isSuccessResponse,
  setAlert,
} from "utils/helper";
import {
  genderOptions,
  userTypes,
  bvnValidationStatuses,
} from "utils/constants";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import {
  nameValidation,
  phoneNumberValidation,
  emailValidation,
  defaultValidation,
} from "utils/validators";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.scss";

const CreateAdminUser = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [adminUserMessage, setAdminUserMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const currentAdmin = location.state?.userInfo;

  useEffect(() => {
    const getRoles = async () => {
      const { data } = await makeRequest(
        "get",
        routes.fetchRoles(`category=ADMIN&predefined=0&size=999&status=ENABLED`)
      );
      let returnedData = data?.data?.content;
      setRoles(
        returnedData?.map((role) => ({
          label: capitalizeFirstLetter(role.name),
          value: role.id,
        }))
      );
    };

    getRoles();
  }, []);

  const getInitialValues = () => {
    let initialValues = {
      firstName: currentAdmin?.firstName || "",
      lastName: currentAdmin?.lastName || "",
      gender: currentAdmin?.gender || "",
      phoneNumber: currentAdmin?.phoneNumber
        ? "0" + currentAdmin.phoneNumber.slice(3)
        : "",
      email: currentAdmin?.email || "",
      roles:
        roles.find(
          (role) =>
            role.label.toLowerCase() === currentAdmin?.roles[0].toLowerCase()
        )?.value || "",
    };
    if (currentAdmin) {
      initialValues.status = currentAdmin.status;
    }

    return initialValues;
  };

  const validationSchema = Yup.object({
    firstName: nameValidation("First Name", "firstName"),
    lastName: nameValidation("Last Name", "lastName"),
    gender: defaultValidation("Gender"),
    email: emailValidation(),
    phoneNumber:
      !currentAdmin && phoneNumberValidation("Phone Number", "phoneNumber"),
    roles: defaultValidation("Role"),
  });

  const handleAdminCreate = async (values, resetForm) => {
    let mobile = values.phoneNumber.toString();

    if (mobile.charAt(0) === "0") {
      mobile = mobile.substring(1);
    }

    let newPhoneNumber = "234" + mobile;

    if (values.status?.toString()) {
      values.status = values.status.toString() === "1" ? "ENABLED" : "DISABLED";
    }

    const newValues = {
      ...values,
      pmNumber: newPhoneNumber,
      phoneNumber: newPhoneNumber,
      bvnConfirmation: bvnValidationStatuses.notPresent,
      userName: values.email,
      userType: userTypes.adminUser,
      dateOfBirth: null,
    };

    newValues.roles = [values.roles.toString()];

    try {
      const method = currentAdmin ? "put" : "post";
      const route = currentAdmin
        ? routes.userUpdate(currentAdmin?.id)
        : routes.userCreation();

      const { data } = await makeRequest(method, route, newValues);

      if (isSuccessResponse(data)) {
        if (currentAdmin) {
          setAlert("Profile updated successfully", "success");
          navigate(-1);
        } else {
          setAdminUserMessage(data.responseMessage);
          setModalOpen(true);
          resetForm();
        }
      } else {
        if (data?.data?.userExists) {
          setAlert("User with email already exists");
        } else {
          setAlert(data.responseMessage);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const actionLabel = currentAdmin ? "Update Admin" : "Create Admin";

  const allowStatusEdit = ["0", "1"].includes(currentAdmin?.status?.toString());

  return (
    <div className="my-4 pt-1">
      <div className="mt-4">
        <BeadCrumb titles={["Dashboard", actionLabel]} />
      </div>

      <div className="create-admin-form mx-auto">
        <div className="text-center">
          <Typography component="h3" variant="heading3" state="primary">
            {actionLabel} User
          </Typography>
          <Typography className="font-danger" variant="body3" state="tetiary">
            You are required to fill all fields
          </Typography>
        </div>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) =>
            handleAdminCreate(values, resetForm)
          }
          enableReinitialize
        >
          {({ errors, touched, getFieldProps, isSubmitting }) => (
            <Form noValidate autoComplete="off">
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <TextField
                    name="firstName"
                    type="text"
                    placeholder="Omolola"
                    label="First Name"
                    {...getFieldProps("firstName")}
                    hasError={errors.firstName && touched.firstName}
                    startIcon={<FaRegUser color="#AAA6A6" />}
                  />
                  <ErrorMessage component={FormError} name="firstName" />
                </div>

                <div className="col-md-6 mb-3">
                  <TextField
                    name="lastName"
                    type="text"
                    placeholder="Dave"
                    label="Last Name"
                    {...getFieldProps("lastName")}
                    hasError={errors.lastName && touched.lastName}
                    startIcon={<FaRegUser color="#AAA6A6" />}
                  />
                  <ErrorMessage component={FormError} name="lastName" />
                </div>
                <div className="col-md-6 mb-3">
                  <Select
                    label="Gender"
                    placeholder="Select gender"
                    options={genderOptions}
                    name="gender"
                    {...getFieldProps("gender")}
                    hasError={errors.gender && touched.gender}
                  />
                  <ErrorMessage component={FormError} name="gender" />
                </div>

                <div className="mb-3 col-md-6">
                  <TextField
                    name="phoneNumber"
                    type="number"
                    placeholder="Enter Phone Number"
                    label="Phone Number"
                    {...getFieldProps("phoneNumber")}
                    hasError={errors.phoneNumber && touched.phoneNumber}
                  />
                  <ErrorMessage component={FormError} name="phoneNumber" />
                </div>

                <div className="mb-3">
                  <TextField
                    name="email"
                    type="email"
                    placeholder="Enter Email Address"
                    label="Email Address"
                    disabled={!!currentAdmin?.email}
                    {...getFieldProps("email")}
                    hasError={errors.email && touched.email}
                    startIcon={<FaRegUser color="#AAA6A6" />}
                  />
                  <ErrorMessage component={FormError} name="email" />
                </div>

                <div
                  className={`mb-3 ${allowStatusEdit ? "col-md-6" : "col-12"}`}
                >
                  <Select
                    label="Assign User Role"
                    placeholder="Select Role"
                    options={roles}
                    name="role"
                    {...getFieldProps("roles")}
                    hasError={errors.roles && touched.roles}
                  />
                  <ErrorMessage component={FormError} name="roles" />
                </div>
                {allowStatusEdit && (
                  <div className="col-md-6 mb-3">
                    <Select
                      label="Status"
                      placeholder="Select Status"
                      options={[
                        { label: "Enabled", value: 1 },
                        { label: "Disabled", value: 0 },
                      ]}
                      name="status"
                      {...getFieldProps("status")}
                    />
                  </div>
                )}
              </div>
              <Button
                type="submit"
                label={actionLabel}
                loading={isSubmitting}
                fullWidth
              />
            </Form>
          )}
        </Formik>
        <RegistrationModal
          showModal={modalOpen}
          adminUser={true}
          adminUserMessage={adminUserMessage}
          onClose={() => setModalOpen(false)}
          modalContentMapper="registrationComplete"
          self={false}
        />
      </div>
    </div>
  );
};

export default CreateAdminUser;
